"use client";

import React from "react";
import { Check, TriangleAlert as AlertTriangle, Zap, Shield, Clock, Wrench, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const services = [{
  category: "Electrical Repair",
  icon: <AlertTriangle className="size-5 text-destructive" />,
  items: ["Circuit breaker trips diagnosis", "Outlet and switch repair", "Light fixture troubleshooting", "Blown fuse replacement", "Damaged wiring repair", "Ask your pro about availability"]
}, {
  category: "Home Wiring & Installation",
  icon: <Wrench className="size-5 text-primary" />,
  items: ["New outlet and switch installation", "Dedicated circuits for appliances", "Ceiling fan installation", "Recessed lighting installation", "Outdoor outlet installation", "Spa and hot tub electrical"]
}, {
  category: "Electrical Upgrades",
  icon: <Zap className="size-5 text-primary" />,
  items: ["Main panel upgrade (100A → 200A)", "Sub-panel installation", "Aluminum-to-copper rewiring", "Knob-and-tube replacement", "Electrical code compliance", "Surge protection installation"]
}, {
  category: "Smart Home & Modern Tech",
  icon: <Shield className="size-5 text-primary" />,
  items: ["Smart thermostat wiring", "Home automation setup", "EV charging station installation", "Security camera wiring", "Network and data lines", "Whole-home surge protection"]
}];
const pricing = [{
  service: "Service Call Assessment",
  price: "Quote from your pro"
}, {
  service: "Outlet/Switch Repair",
  price: "Quote from your pro"
}, {
  service: "Ceiling Fan Installation",
  price: "Quote from your pro"
}, {
  service: "Recessed Lighting",
  price: "Quote from your pro"
}, {
  service: "Panel Upgrade",
  price: "Quote from your pro"
}, {
  service: "Full Rewiring (full home)",
  price: "Quote from your pro"
}];
const commonProblems = [{
  issue: "Breaker keeps tripping?",
  cause: "Overloaded circuit, short circuit, or ground fault.",
  time: "1–2 hours",
  cost: "Quote from your pro"
}, {
  issue: "Outlets not working in part of home?",
  cause: "Tripped GFCI, breaker issue, or wiring problem.",
  time: "30–90 minutes",
  cost: "Quote from your pro"
}, {
  issue: "House wiring outdated (aluminum or cloth)?",
  cause: "Age, wear, or modern capacity requirements.",
  time: "Full house rewiring: multi-day project",
  cost: "Quote from your pro"
}, {
  issue: "Panel needs upgrade?",
  cause: "Older panel insufficient for modern appliances/EV charger.",
  time: "1–2 days",
  cost: "Quote from your pro"
}];
const faqs = [{
  q: "Is it safe to DIY electrical work?",
  a: "No. Complex electrical work should be performed by experienced local pros per local codes. Working with electrical systems can lead to shock and fire hazards."
}, {
  q: "Do pros pull permits for electrical work?",
  a: "Yes. Major electrical work is pulled through required local permits and inspections to ensure code compliance and safety."
}, {
  q: "How much does a panel upgrade cost?",
  a: "Pricing depends on your existing service capacity, panel location, and local requirements. Your pro gives a quote before work starts."
}, {
  q: "Can pros install EV charging at home?",
  a: "Yes. Local pros can evaluate your panel capacity and install dedicated Level 2 EV charging circuits."
}, {
  q: "What's a dedicated circuit?",
  a: "An individual circuit for a high-draw appliance (like an oven, washer, or dryer) to prevent overloads and ensure safe operation."
}, {
  q: "How often should I have my home's electrical inspected?",
  a: "Every 3–5 years, or before a major appliance installation. Inspections are especially recommended for older homes."
}];
function ElectricalBlogPosts() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="animate-pulse">
            <div className="h-48 bg-muted rounded-t-xl" />
            <CardHeader>
              <div className="h-6 bg-muted rounded w-3/4 mb-2" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-muted rounded w-full mb-2" />
              <div className="h-4 bg-muted rounded w-5/6" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => <Card key={post.id} data-index={i} className="flex flex-col h-full overflow-hidden hover:shadow-md transition-shadow">
          {post.featuredImage && <div className="aspect-video w-full overflow-hidden bg-muted">
              <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
            </div>}
          <CardHeader>
            <CardTitle className="text-lg line-clamp-2 font-default">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
            {post.date && <CardDescription>
                {new Date(post.date).toLocaleDateString()}
              </CardDescription>}
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {post.excerpt && <div className="text-sm text-muted-foreground line-clamp-3 mb-4" dangerouslySetInnerHTML={{
          __html: post.excerpt
        }} />}
            <div className="mt-auto pt-4">
              <Link to={post.link || "#"}>
                <Button variant="outline" size="sm" className="w-full">
                  Read Article
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
export default function Electrical() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="electricalsafetyandserviceguide" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4">
            Safety & Service Guide
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-6">
            Professional Electrical Service for Your Home
          </h1>
          <p className="text-lg text-muted-foreground">
            Electrical problems are serious — a malfunctioning circuit, outdated wiring, or overloaded panels 
            can create safety hazards, equipment damage, and unexpected repair costs. Connect with experienced local pros 
            for dependable, code-compliant solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {}
          <div className="lg:col-span-2 space-y-12">
            
            {}
            <div>
              <h2 className="text-2xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
                <Zap className="size-6 text-primary" />
                Electrical Services Available
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((service, i) => <Card key={i} data-index={i} className="border-border/50 shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-muted rounded-lg">
                          {service.icon}
                        </div>
                        <CardTitle className="text-lg font-default">{service.category}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.items.map((item, j) => <li key={j} data-index={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Check className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>)}
              </div>
            </div>

            <Separator />

            {}
            <div>
              <h2 className="text-2xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
                <AlertTriangle className="size-6 text-destructive" />
                Common Electrical Issues Local Pros Resolve
              </h2>
              <div className="space-y-6">
                {commonProblems.map((problem, i) => <div key={i} data-index={i} className="bg-card border border-border/50 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold font-default text-foreground mb-2">
                      {problem.issue}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      <span className="font-medium text-foreground">Usually:</span> {problem.cause}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-md">
                        <Clock className="size-4" />
                        <span>Fix time: {problem.time}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-md">
                        <Zap className="size-4" />
                        <span>Cost: {problem.cost}</span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <Separator />

            {}
            <div>
              <h2 className="text-2xl font-semibold font-default text-foreground mb-6">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left font-default text-base">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

          </div>

          {}
          <div className="space-y-8">
            
            {}
            <Card className="border-primary/20 shadow-md bg-card/50 backdrop-blur-sm">
              <CardHeader className="bg-primary/5 border-b border-border/50 pb-4">
                <CardTitle className="font-default text-xl flex items-center gap-2">
                  Transparent Pricing
                </CardTitle>
                <CardDescription>
                  Your pro gives a quote before work starts.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/50">
                  {pricing.map((item, i) => <div key={i} data-index={i} className="flex justify-between items-center p-4 hover:bg-muted/30 transition-colors">
                      <span className="text-sm font-medium text-foreground">{item.service}</span>
                      <span className="text-sm text-muted-foreground text-right pl-4">{item.price}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {}
            <Card className="bg-secondary text-secondary-foreground border-none shadow-lg">
              <CardHeader>
                <CardTitle className="font-default text-xl">Why Handy Heroes?</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {["Experienced independent local electrical pros", "Code-compliant workmanship on every project", "Ask your pro about scheduling availability", "Warranty terms set by pro — confirm before work begins", "Clear quotes provided before work starts"].map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <div className="bg-primary/20 p-1 rounded-lg mt-0.5">
                        <Check className="size-3 text-primary-foreground" />
                      </div>
                      <span className="text-sm opacity-90">{benefit}</span>
                    </li>)}
                </ul>
                <div className="mt-8">
                  <Link to="#bookingrequest">
                    <Button variant="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                      Request a Quote
                      <ChevronRight className="size-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {}
            <div className="rounded-xl overflow-hidden shadow-md border border-border/50">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_panel_upgrade.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_panel_upgrade.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_panel_upgrade.webp-wvc-sizes" alt="Independent electrical pro working on residential electrical panel" className="w-full h-auto object-cover aspect-[4/3]" loading="lazy" />
            </div>

          </div>
        </div>

        {}
        <div className="mt-24 pt-16 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-default text-foreground mb-2">
                Latest Electrical Insights
              </h2>
              <p className="text-muted-foreground">
                Read our latest articles on electrical safety, maintenance, and smart home upgrades.
              </p>
            </div>
            <Link to="/HomePage" className="text-foreground">
              <Button variant="outline">
                View All Articles
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date",
          status: ["publish"]
        }}>
            <ElectricalBlogPosts />
          </PostsProvider>
        </div>

      </div>
    </section>;
}